import React from 'react';
import { observer } from 'mobx-react';
import { v4 as uuid } from 'uuid';
import { FormattedMessage, MessageDescriptor } from 'react-intl';
import { FormField } from '../form-field/form-field';
import { FormFieldError } from './form-field-error';
import { FormFieldProps } from '../../../../infrastructure/form-viewmodel-controller';
import { REQUIRED_FIELD_TEXT } from '../../../../constants/common-labels.constants';

interface IOption {
    value: string | number;
    label: string;
    disabled?: boolean;
}

interface IProps {
    field: FormFieldProps;
    labelDescriptor: MessageDescriptor;
    placeholderOption: boolean;
    options?: IOption[];
    placeholderOptionText?: string;
    customOptions?: JSX.Element;
    customChangeAction?: (e: string | number | string[]) => void;
    fieldClassName?: string;
    withError?: boolean;
    optionsSeparator?: number[];
    placeholderOptionSeparator?: boolean;
    disabled?: boolean;
    required?: boolean;
}

export const FormFieldInputSelect = observer(({
    field, labelDescriptor, placeholderOption, placeholderOptionText,
    customOptions, options, customChangeAction, fieldClassName, withError,
    optionsSeparator = [], placeholderOptionSeparator, disabled, required
}: IProps): JSX.Element => {

    const handleChange = (value: string | number | string[]): void => {
        if (customChangeAction && typeof customChangeAction === 'function') {
            customChangeAction(value);
        } else {
            field.setValue(value);
        }
    };

    const separator = <hr/>;

    return (
        <FormField
            label={<>
                <FormattedMessage {...labelDescriptor} />
                {required && <span className='oe-form-field__title-required'><FormattedMessage {...REQUIRED_FIELD_TEXT} /></span>}
            </>}
            className={fieldClassName}
        >
            <select
                className='oe-form-field__control'
                value={field.value as string}
                onChange={e => handleChange(e.target.value)}
                onBlur={field.setTouched}
                disabled={disabled}
            >
                {placeholderOption && <>
                    <option value={''}>
                        {placeholderOptionText}
                    </option>
                    {placeholderOptionSeparator && separator}
                </>}
                {customOptions
                    ? customOptions
                    : options?.map(({ value, label, disabled: optionDisabled }, i) => {
                        return <React.Fragment key={uuid()}>
                            <option value={value} key={value as string + i} disabled={optionDisabled}>{label}</option>
                            {optionsSeparator.includes(i) && separator}
                        </React.Fragment>;
                    })
                }
            </select>
            {withError && <FormFieldError field={field} labelDescriptor={labelDescriptor} />}
        </FormField>
    );
});
